import { type Product } from '@/services/products/types';
import { type TrolleyLineItem } from '@/types/api-types';
import { type ElementType, useState } from 'react';
import { Typography } from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import AddToTrolleyButton from '@/components/AddToTrolleyButton/AddToTrolleyButton';
import { clsx } from 'clsx';
import eventBus, { EventName } from '@/services/event-bus';
import ProductPrice from '@/components/ProductPrice/ProductPrice';
import styles from './ProductPod.module.scss';
import { ProductPodImage } from './ProductPodImage/ProductPodImage';
import { ProductPodHeader } from './ProductPodHeader/ProductPodHeader';
import { ProductReviewAndDiscount } from './ProductReviewAndDiscount/ProductReviewAndDiscount';
import JsonLDSchema, { type ProductSchema } from '../JsonLDMeta/JsonLDMeta';
import useProductSchema from '../JsonLDMeta/use-product-schema';

interface Props {
  position: number;
  product: Product;
  lineItem?: TrolleyLineItem;
  loading: boolean;
  addItem: (product: Product, quantity: number) => Promise<void>;
  updateProductQuantity: (lineItemId: string, productId: string, quantity: number) => Promise<void>;
  horizontalGrid?: boolean;
  responsiveGrid?: boolean;
  titleElement?: ElementType;
  listType?: string;
  titleId?: string | null;
}

export function ProductPod({
  addItem,
  horizontalGrid,
  lineItem,
  listType,
  loading,
  position,
  product,
  responsiveGrid,
  titleElement = 'h2',
  titleId,
  updateProductQuantity,
}: Props) {
  const { availability, formattedPrice, image, size, totalPounds } = product;
  const sizeToDisplay = size === '75cl' ? undefined : size;
  const [insufficientQuantity, setInsufficientQuantity] = useState<boolean>(false);
  const isProductInTrolley = lineItem && lineItem.quantity > 0;

  const eventDetails = {
    item: product,
    listName: listType ?? 'PLP',
    position,
  };
  const productSchema = useProductSchema(product);
  return (
    <>
      <JsonLDSchema<ProductSchema> schema={productSchema} />
      <article
        className={clsx(styles.container, {
          [styles.addedToTrolley]: isProductInTrolley && !insufficientQuantity,
          [styles.horizontalGrid]: horizontalGrid,
          [styles.insufficientQuantity]: insufficientQuantity,
          [styles.responsiveGrid]: responsiveGrid,
        })}
        data-testid="plp-product"
      >
        {isProductInTrolley && !insufficientQuantity && (
          <div className={styles.itemInTrolley}>{lineItem.quantity} in trolley</div>
        )}
        {lineItem && insufficientQuantity && (
          <div className={styles.insufficientQuantityMessage}>
            {`Only ${lineItem.availableQuantity} ${
              lineItem.availableQuantity > 1 ? 'bottles' : 'bottle'
            } left in stock. Please reduce the amount`}
          </div>
        )}
        <div className={styles.image}>
          {image && (
            <ProductPodImage
              isProductInTrolley={isProductInTrolley}
              onRedirect={() => eventBus.emit(EventName.SELECT_LIST_ITEM, eventDetails)}
              product={product}
            />
          )}
        </div>
        <div className={styles.header}>
          <ProductPodHeader
            onRedirect={() => eventBus.emit(EventName.SELECT_LIST_ITEM, eventDetails)}
            product={product}
            titleElement={titleElement}
            titleId={titleId}
          />
        </div>
        <div className={styles.ratingAndDiscount}>
          <ProductReviewAndDiscount product={product} />
        </div>
        <div className={styles.lowerAttributeContainer}>
          {                                                                                                           }
          <ProductPrice layout="stacked" priceToPayStyle="screenHeadingSmall" product={product} />
          {sizeToDisplay && (
            <Typography
              data-testid="pp-product-size"
              element="span"
              noMargins
              styleAs="paragraph"
              textStyle="body-m"
            >
              {sizeToDisplay}
            </Typography>
          )}
        </div>
        {availability.isOnStock && formattedPrice ? (
          <div className={styles.controls}>
            <AddToTrolleyButton
              addProductToTrolley={addItem}
              buttonTheme="secondary"
              disabled={totalPounds === null}
              lineItem={lineItem}
              loading={loading}
              position={position}
              product={product}
              setInsufficientQuantity={setInsufficientQuantity}
              updateProductQuantity={updateProductQuantity}
            />
          </div>
        ) : (
          <div className={styles.outOfStock}>
            {availability.isOnStock ? 'Temporarily unavailable' : 'Sold out online'}
          </div>
        )}
      </article>
    </>
  );
}

export default ProductPod;
